const DATASET_VERSION = '2026-08-12-v1';
const STORAGE_KEY = `cgau-field-notes:${DATASET_VERSION}`;
const PALETTES = new Set(['perimeter', 'camera', 'digital', 'vehicle', 'safety']);
const SAFE_ID = /^[a-z0-9-]+$/;
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';
const MEASUREMENT_CHUNK_SIZE = 6;

let volatileState = null;

const randomIndex = (length) => {
  if (length <= 1) return 0;

  if (globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    const ceiling = Math.floor(0x100000000 / length) * length;
    do globalThis.crypto.getRandomValues(values);
    while (values[0] >= ceiling);
    return values[0] % length;
  }

  return Math.floor(Math.random() * length);
};

export const shuffleIds = (ids, pickIndex = randomIndex) => {
  const shuffled = [...ids];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = pickIndex(index + 1);
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
};

export const normaliseBagState = (candidate, ids, version = DATASET_VERSION) => {
  const allowed = new Set(ids);
  const state = candidate && typeof candidate === 'object' ? candidate : {};
  const matchingVersion = state.version === version;
  const last = matchingVersion && allowed.has(state.last) ? state.last : null;
  const seen = new Set();
  const remaining = matchingVersion && Array.isArray(state.remaining)
    ? state.remaining.filter((id) => {
        if (!allowed.has(id) || seen.has(id) || id === last) return false;
        seen.add(id);
        return true;
      })
    : [];

  return { version, remaining, last };
};

export const drawFromBag = (candidate, ids, options = {}) => {
  const version = options.version || DATASET_VERSION;
  const pickIndex = options.pickIndex || randomIndex;
  const state = normaliseBagState(candidate, ids, version);

  if (!ids.length) return { id: null, state };

  if (!state.remaining.length) {
    state.remaining = shuffleIds(ids, pickIndex);
    if (state.remaining.length > 1 && state.remaining[0] === state.last) {
      const swapIndex = state.remaining.findIndex((id) => id !== state.last);
      [state.remaining[0], state.remaining[swapIndex]] = [state.remaining[swapIndex], state.remaining[0]];
    }
  }

  const id = state.remaining.shift();
  state.last = id;
  return { id, state };
};

const readStoredState = () => {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_error) {
    // Privacy modes and storage policies may block localStorage. The in-memory
    // bag below preserves a good session without making the component fail.
  }
  return volatileState;
};

const writeStoredState = (state) => {
  volatileState = state;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_error) {
    // The in-memory state is already updated.
  }
};

const validSourceUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch (_error) {
    return null;
  }
};

const asText = (value) => (typeof value === 'string' ? value.trim() : '');

export const validateNotes = (records, symbolExists = () => true) => {
  if (!Array.isArray(records)) return [];

  const seen = new Set();
  return records.filter((record) => {
    if (!record || typeof record !== 'object') return false;
    const id = asText(record.id);
    const scene = asText(record.scene);
    const sourceUrl = validSourceUrl(record.sourceUrl);
    const requiredText = ['category', 'title', 'body', 'action', 'sourceLabel', 'reviewedAt'];

    if (!SAFE_ID.test(id) || !SAFE_ID.test(scene) || seen.has(id)) return false;
    if (!Number.isInteger(record.number) || record.number < 1) return false;
    if (!PALETTES.has(record.palette) || !sourceUrl || !symbolExists(scene)) return false;
    if (requiredText.some((key) => !asText(record[key]))) return false;

    record.id = id;
    record.scene = scene;
    record.sourceUrl = sourceUrl;
    seen.add(id);
    return true;
  });
};

const formatReviewedDate = (value) => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const BaseElement = globalThis.HTMLElement || class {};

class CamGuardFieldNotes extends BaseElement {
  connectedCallback() {
    if (this._active) return;
    this._active = true;

    this._button = this.querySelector('[data-cgfn-next]');
    this._card = this.querySelector('[data-cgfn-card]');
    this._copy = this.querySelector('.cgau-fn-copy');
    this._counter = this.querySelector('[data-cgfn-counter]');
    this._category = this.querySelector('[data-cgfn-category]');
    this._title = this.querySelector('[data-cgfn-title]');
    this._body = this.querySelector('[data-cgfn-body]');
    this._action = this.querySelector('[data-cgfn-action]');
    this._source = this.querySelector('[data-cgfn-source]');
    this._reviewed = this.querySelector('[data-cgfn-reviewed]');
    this._sceneUse = this.querySelector('[data-cgfn-scene-use]');
    this._announcer = this.querySelector('[data-cgfn-announcer]');
    this._data = this.querySelector('[data-cgfn-data]');
    this._symbolPrefix = this.dataset.symbolPrefix || '';
    this._motionMedia = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this._reduceMotion = this._motionMedia?.matches || false;
    this._designMode = Boolean(globalThis.Shopify?.designMode);

    if (!this._button || !this._card || !this._data) return;

    let records = [];
    try {
      records = JSON.parse(this._data.textContent || '[]');
    } catch (_error) {
      return;
    }

    this._notes = validateNotes(records, (scene) => Boolean(document.getElementById(`${this._symbolPrefix}${scene}`)));
    this._notesById = new Map(this._notes.map((note) => [note.id, note]));
    this._ids = this._notes.map((note) => note.id);
    if (!this._notes.length) return;

    this._button.hidden = false;
    this.dataset.enhanced = 'true';
    this._onNext = () => this.showNext({ announce: true, transition: true });
    this._button.addEventListener('click', this._onNext);
    this._onMotionChange = () => {
      this._reduceMotion = this._motionMedia?.matches || false;
      if (this._reduceMotion) this._finishNoteTransition();
    };
    this._onVisibility = () => {
      if (document.hidden) {
        this._finishNoteTransition();
        this._cancelHeightMeasurement();
      } else if (this._measureNearViewport) {
        this._scheduleHeightMeasurement(false);
      }
    };
    this._motionMedia?.addEventListener('change', this._onMotionChange);
    document.addEventListener('visibilitychange', this._onVisibility);
    this._inView = !globalThis.IntersectionObserver;
    this._motionObserver = globalThis.IntersectionObserver
      ? new IntersectionObserver(([entry]) => {
          this._inView = entry.isIntersecting;
          if (!this._inView) this._finishNoteTransition();
        })
      : null;
    this._motionObserver?.observe(this._card);
    this._measureNearViewport = !globalThis.IntersectionObserver;
    this._measureObserver = globalThis.IntersectionObserver
      ? new IntersectionObserver(([entry]) => {
          this._measureNearViewport = entry.isIntersecting;
          if (this._measureNearViewport) this._scheduleHeightMeasurement(false);
          else this._cancelHeightMeasurement();
        }, { rootMargin: '800px 0px', threshold: 0 })
      : null;
    this._measureObserver?.observe(this._card);

    if (this._currentId && this._notesById.has(this._currentId)) {
      this.render(this._notesById.get(this._currentId), false);
    } else {
      this.showNext({ announce: false, transition: false });
    }

    this._scheduleHeightMeasurement(true);
    this._resizeObserver = globalThis.ResizeObserver
      ? new globalThis.ResizeObserver((entries) => {
          const width = entries[0]?.contentRect?.width || 0;
          if (!width || Math.abs(width - (this._observedWidth || 0)) < 1) return;
          this._observedWidth = width;

          // Measure once at the first real width change so rotation does not
          // leave the old reserved height visible, then once more after the
          // resize burst settles at its final width.
          if (!this._resizeMeasureActive) {
            this._resizeMeasureActive = true;
            this._scheduleHeightMeasurement(false);
          }
          globalThis.clearTimeout?.(this._resizeMeasureTimer);
          this._resizeMeasureTimer = globalThis.setTimeout?.(() => {
            this._resizeMeasureActive = false;
            if (this.isConnected) this._scheduleHeightMeasurement(false);
          }, 160);
        })
      : null;
    this._resizeObserver?.observe(this._card);
    globalThis.document?.fonts?.ready?.then(() => {
      if (this.isConnected) this._scheduleHeightMeasurement(true);
    });
  }

  disconnectedCallback() {
    this._finishNoteTransition();
    this._active = false;
    this._button?.removeEventListener('click', this._onNext);
    globalThis.clearTimeout?.(this._transitionTimer);
    globalThis.clearTimeout?.(this._resizeMeasureTimer);
    this._cancelHeightMeasurement();
    this._resizeObserver?.disconnect();
    this._motionObserver?.disconnect();
    this._measureObserver?.disconnect();
    this._motionMedia?.removeEventListener('change', this._onMotionChange);
    document.removeEventListener('visibilitychange', this._onVisibility);
    this._resizeMeasureActive = false;
    this._card?.classList.remove('is-changing', 'is-arriving');
    this._button?.removeAttribute('aria-disabled');
    this._transitioning = false;
  }

  _scheduleHeightMeasurement(force) {
    if (!this._measureNearViewport || !this._card) return;
    this._measureForce = this._measureForce || Boolean(force);
    this._cancelHeightMeasurement(false);
    globalThis.cancelAnimationFrame?.(this._measureFrame);
    this._measureFrame = globalThis.requestAnimationFrame?.(() => {
      const shouldForce = this._measureForce;
      this._measureForce = false;
      this._startHeightMeasurement(shouldForce);
    });
  }

  _cancelHeightMeasurement(resetForce = true) {
    this._measureGeneration = (this._measureGeneration || 0) + 1;
    globalThis.cancelAnimationFrame?.(this._measureFrame);
    this._measureFrame = null;
    if (this._measureIdleId != null) {
      if (this._measureIdleType === 'idle') globalThis.cancelIdleCallback?.(this._measureIdleId);
      else globalThis.clearTimeout?.(this._measureIdleId);
    }
    this._measureIdleId = null;
    this._measureIdleType = null;
    this._measureJob?.probes?.forEach(({ probe }) => probe.remove());
    this._measureJob = null;
    if (resetForce) this._measureForce = false;
  }

  _queueMeasurementWork(callback) {
    const run = (deadline) => {
      this._measureIdleId = null;
      this._measureIdleType = null;
      if (this.isConnected && this._measureNearViewport) callback(deadline);
    };
    if (typeof globalThis.requestIdleCallback === 'function') {
      this._measureIdleType = 'idle';
      this._measureIdleId = globalThis.requestIdleCallback(run, { timeout: 500 });
    } else {
      this._measureIdleType = 'timeout';
      this._measureIdleId = globalThis.setTimeout?.(() => run({ timeRemaining: () => 8 }), 0);
    }
  }

  _startHeightMeasurement(force) {
    if (!this._measureNearViewport) return;
    const width = this._card?.getBoundingClientRect().width || 0;
    if (!width || (!force && Math.abs(width - (this._measuredWidth || 0)) < 1)) return;

    this._cancelHeightMeasurement(false);
    const generation = this._measureGeneration;
    const job = {
      generation,
      width,
      index: 0,
      probes: [],
      tallest: 0,
      tallestCopy: 0,
    };
    this._measureJob = job;
    this._queueMeasurementWork((deadline) => this._runMeasurementChunk(job, deadline));
  }

  _createMeasurementProbe(note, width) {
    const probe = this._card.cloneNode(true);
    probe.classList.remove('is-changing', 'is-arriving');
    probe.removeAttribute('aria-labelledby');
    probe.setAttribute('aria-hidden', 'true');
    probe.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    // The artwork has a CSS-defined footprint, so its nested SVG <use>
    // shadow tree is irrelevant to height and need not be instantiated 50x.
    probe.querySelector('[data-cgfn-scene-use]')?.remove();
    probe.style.removeProperty('--cgfn-copy-stable-height');
    Object.assign(probe.style, {
      position: 'absolute',
      top: '0',
      left: '-10000px',
      width: `${width}px`,
      height: 'auto',
      minHeight: '0',
      visibility: 'hidden',
      pointerEvents: 'none',
    });

    const title = probe.querySelector('[data-cgfn-title]');
    const body = probe.querySelector('[data-cgfn-body]');
    const action = probe.querySelector('[data-cgfn-action]');
    const category = probe.querySelector('[data-cgfn-category]');
    const source = probe.querySelector('[data-cgfn-source]');
    const reviewed = probe.querySelector('[data-cgfn-reviewed]');
    title.textContent = note.title;
    body.textContent = note.body;
    action.textContent = note.action;
    category.textContent = note.category;
    source.textContent = note.sourceLabel;
    reviewed.textContent = `Reviewed ${formatReviewedDate(note.reviewedAt)}`;
    return { probe, copy: probe.querySelector('.cgau-fn-copy') };
  }

  _runMeasurementChunk(job) {
    if (job !== this._measureJob || job.generation !== this._measureGeneration) return;
    const end = Math.min(job.index + MEASUREMENT_CHUNK_SIZE, this._notes.length);
    const fragment = document.createDocumentFragment();
    for (; job.index < end; job.index += 1) {
      const probe = this._createMeasurementProbe(this._notes[job.index], job.width);
      job.probes.push(probe);
      fragment.append(probe.probe);
    }

    // Keep each layout flush bounded: append, read, and remove only this chunk.
    this.append(fragment);
    job.probes.forEach(({ probe, copy }) => {
      job.tallest = Math.max(job.tallest, probe.getBoundingClientRect().height);
      job.tallestCopy = Math.max(job.tallestCopy, copy?.getBoundingClientRect().height || 0);
    });
    job.probes.forEach(({ probe }) => probe.remove());
    job.probes = [];

    if (job.index < this._notes.length) {
      this._queueMeasurementWork((deadline) => this._runMeasurementChunk(job, deadline));
      return;
    }

    this._measureJob = null;
    this._measuredWidth = job.width;
    if (job.tallest) this._card.style.minHeight = `${Math.ceil(job.tallest)}px`;
    if (job.tallestCopy) {
      this._card.style.setProperty('--cgfn-copy-stable-height', `${Math.ceil(job.tallestCopy)}px`);
    }
  }

  draw() {
    const currentState = this._designMode ? this._editorState : readStoredState();
    const result = drawFromBag(currentState, this._ids);
    if (this._designMode) this._editorState = result.state;
    else writeStoredState(result.state);
    return this._notesById.get(result.id);
  }

  showNext({ announce, transition }) {
    if (this._transitioning) return;
    const note = this.draw();
    if (!note) return;

    if (!transition || this._reduceMotion || !this._inView || document.hidden) {
      this.render(note, announce);
      return;
    }

    this._transitioning = true;
    this._pendingNote = { note, announce };
    this._button.setAttribute('aria-disabled', 'true');
    this._card.classList.add('is-changing');
    this._transitionTimer = globalThis.setTimeout(() => {
      this.render(note, announce);
      this._pendingNote = null;
      this._card.classList.remove('is-changing');
      this._card.classList.add('is-arriving');
      this._transitionTimer = globalThis.setTimeout(() => this._finishNoteTransition(), 240);
    }, 180);
  }

  _finishNoteTransition() {
    globalThis.clearTimeout?.(this._transitionTimer);
    if (this._pendingNote) {
      const { note, announce } = this._pendingNote;
      this._pendingNote = null;
      this.render(note, announce);
    }
    this._card?.classList.remove('is-changing', 'is-arriving');
    this._button?.removeAttribute('aria-disabled');
    this._transitioning = false;
  }

  render(note, announce) {
    this._currentId = note.id;
    this._card.dataset.palette = note.palette;
    this._counter.textContent = `Note ${String(note.number).padStart(2, '0')} / ${this._notes.length}`;
    this._category.textContent = note.category;
    this._title.textContent = note.title;
    this._body.textContent = note.body;
    this._action.textContent = note.action;
    this._source.textContent = note.sourceLabel;
    this._source.href = note.sourceUrl;
    this._source.setAttribute('aria-label', `Read the guidance from ${note.sourceLabel}`);
    this._reviewed.textContent = `Reviewed ${formatReviewedDate(note.reviewedAt)}`;
    this._reviewed.dateTime = note.reviewedAt;

    const symbolReference = `#${this._symbolPrefix}${note.scene}`;
    this._sceneUse.setAttribute('href', symbolReference);
    this._sceneUse.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', symbolReference);

    const reservedCopyHeight = Number.parseFloat(
      this._card.style.getPropertyValue('--cgfn-copy-stable-height'),
    );
    if (Number.isFinite(reservedCopyHeight) && this._copy?.scrollHeight > reservedCopyHeight + 1) {
      this._scheduleHeightMeasurement(true);
    }

    if (announce && this._announcer) {
      this._announcer.textContent = '';
      globalThis.requestAnimationFrame?.(() => {
        this._announcer.textContent = `Field note ${note.number} of ${this._notes.length}: ${note.title}`;
      });
    }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('cgau-field-notes')) {
  globalThis.customElements.define('cgau-field-notes', CamGuardFieldNotes);
}

export { CamGuardFieldNotes, DATASET_VERSION };
